const MASKED_VALUE = '***'

/**
 * Keys that hold patient data wherever they appear in a payload. Matched exactly
 * (lowercased), so `mrn_chars` on the hospital config is untouched while `mrn` is not.
 *
 * `hl7` carries the raw ADT message, whose PID segment contains full demographics.
 * `transcript`, `report`, the `*note` fields, `aimee_memory_*` and `ai_snapshot*` are free
 * text written about a patient; `precautions` and `pain_scale` are clinical state.
 *
 * The patient fields mirror `V1::PatientsController#patient_params` — check that list when
 * it changes, since the whole patient object is sent on create and update.
 */
const PHI_KEYS = new Set([
  'display_name',
  'first_name',
  'last_name',
  'middle_name',
  'patient_name',
  'family_name',
  'given_name',
  'suffix',
  'mrn',
  'prior_mrn',
  'dob',
  'date_of_birth',
  'birth_date',
  'note',
  'room_note',
  'family_note',
  'aimee_memory_personal',
  'aimee_memory_clinical',
  'precautions',
  'pain_scale',
  'custom_fields',
  'ai_snapshot',
  'ai_snapshot_scorecard',
  'transcript',
  'report',
  'hl7',
  'csn',
  'patient_id',
  'patient_monitor_id'
])

/**
 * Objects under these keys describe a patient or a monitoring session, so a bare `name`
 * or `id` inside one identifies a patient — several endpoints serialize the name that way
 * (v1/virtual_requests/index, v1/user_activities/_meeting, v1/patient_monitor_connections/index).
 * Context does not survive a step into any other key, so room.name, participant.name,
 * tag.name and their ids stay readable.
 */
const PATIENT_CONTAINER_KEYS = new Set([
  'patient',
  'patients',
  'dup_patient',
  'patient_monitor',
  'patient_monitors'
])
const CONTEXTUAL_PHI_KEYS = new Set(['name', 'id'])

/**
 * Envelope keys that wrap the rows of a paginated response. They carry patient context
 * through to the rows, so that on a patients endpoint `results[].id` is the patient id.
 */
const PASSTHROUGH_KEYS = new Set(['results', 'data'])

/**
 * Endpoints whose payload is patients or monitoring sessions at the top level, where a
 * bare `id` is therefore a patient id. Matches the collection and a single nested
 * segment (`/v1/patients`, `/v1/patients/4821`, `/v1/patients/dropdown`) but not a
 * sub-resource like `/v1/patients/4821/forms`, whose rows are not patients.
 */
const PATIENT_ROOT_URL = /\/v1\/(patients|patient_monitors)(\/[^/?#]+)?\/?(\?|#|$)/i

const redactNode = (node, inPatient) => {
  if (Array.isArray(node)) {
    return node.map(item => redactNode(item, inPatient))
  }
  if (node === null || typeof node !== 'object') {
    return node
  }

  const result = {}
  for (const [key, value] of Object.entries(node)) {
    const lowerKey = key.toLowerCase()
    const isPhi = PHI_KEYS.has(lowerKey) || (inPatient && CONTEXTUAL_PHI_KEYS.has(lowerKey))

    if (isPhi) {
      result[key] = value === null || value === undefined ? value : MASKED_VALUE
    } else {
      const childInPatient = PATIENT_CONTAINER_KEYS.has(lowerKey) ||
        (inPatient && PASSTHROUGH_KEYS.has(lowerKey))
      result[key] = redactNode(value, childInPatient)
    }
  }
  return result
}

/**
 * Strips patient data out of a request or response body before it reaches LogRocket.
 * Keys are matched by name, so a payload that reaches PHI through a join under a key
 * not listed above will not be caught — add the key when that happens.
 *
 * A string body that is not JSON cannot be walked, so it is dropped rather than sent.
 *
 * @param {*} body - body as LogRocket supplies it (a JSON string), or empty
 * @param {string} [url] - the request URL, used to tell whether the rows are patients
 * @returns {*} the body with patient fields replaced by '***'
 */
export const redactPhi = (body, url) => {
  if (!body) {
    return body
  }

  const rootIsPatient = typeof url === 'string' && PATIENT_ROOT_URL.test(url)

  if (typeof body === 'string') {
    try {
      return JSON.stringify(redactNode(JSON.parse(body), rootIsPatient))
    } catch {
      return MASKED_VALUE
    }
  }

  if (typeof body === 'object') {
    return redactNode(body, rootIsPatient)
  }

  return body
}
